import React from "react";

// Single source of truth for service categories. Keep in sync with InputChecklist.
export const SERVICE_CATEGORIES = [
    { code: "MEC", label: "Mecânica", icon: "🔧" },
    { code: "ELE", label: "Elétrica", icon: "⚡" },
    { code: "FUN", label: "Funilaria", icon: "🔨" },
    { code: "PIN", label: "Pintura", icon: "🎨" },
    { code: "TAP", label: "Tapeçaria", icon: "🪑" },
    { code: "LAV", label: "Lavagem", icon: "💧" },
    { code: "POL", label: "Polimento", icon: "✨" },
    { code: "MAR", label: "Martelinho", icon: "🔩" },
    { code: "VIT", label: "Vitrificação", icon: "💎" },
    { code: "TER", label: "Terceiros", icon: "🤝" },
    { code: "OUT", label: "Outros", icon: "📋" },
    { code: "ACA", label: "Acabamento", icon: "🖌️" },
    { code: "ALM", label: "Almoxarifado", icon: "📦" },
];

const CATEGORY_BY_CODE = SERVICE_CATEGORIES.reduce((acc, c) => {
    acc[c.code] = c;
    return acc;
}, {});

/**
 * Normalize whatever shape the backend gives us into an array of codes.
 *
 * Accepts:
 *   - array of strings:  ["MEC", "ELE"]
 *   - JSON-encoded string of an array: '["MEC","ELE"]'
 *   - null / undefined / empty → []
 */
export function normalizeCategoryCodes(raw) {
    if (!raw) return [];
    if (Array.isArray(raw)) {
        return raw.filter((c) => typeof c === "string" && c.trim()).map((c) => c.trim());
    }
    if (typeof raw === "string") {
        const trimmed = raw.trim();
        if (!trimmed) return [];
        try {
            const parsed = JSON.parse(trimmed);
            if (Array.isArray(parsed)) return normalizeCategoryCodes(parsed);
        } catch (_) {
            /* fall through */
        }
        // Could also be comma-separated: "MEC,ELE"
        return trimmed
            .split(",")
            .map((s) => s.trim())
            .filter(Boolean);
    }
    return [];
}

/**
 * Renders the chosen service categories as pill badges, styled like the
 * selection buttons in InputChecklist (no click handlers — read-only display).
 *
 * Props:
 *   codes     — array of category codes (or any shape supported by normalizeCategoryCodes)
 *   size      — "sm" | "md" (default "md")
 *   showLabel — boolean (default true). When false, only icon+code show.
 *   className — extra classes on the outer wrapper
 *   emptyText — what to render when there are no categories (default: nothing)
 */
export default function ServiceCategoryBadges({
    codes,
    size = "md",
    showLabel = true,
    className = "",
    emptyText = null,
}) {
    const list = normalizeCategoryCodes(codes);
    if (list.length === 0) {
        return emptyText ? <span className="text-sm text-gray-400 italic">{emptyText}</span> : null;
    }

    const sizing =
        size === "sm"
            ? "px-2 py-1 text-xs gap-1"
            : "px-3 py-1.5 text-sm gap-1.5";

    return (
        <div className={`flex flex-wrap gap-2 ${className}`}>
            {list.map((code) => {
                const cat = CATEGORY_BY_CODE[code] || { code, label: code, icon: "" };
                return (
                    <span
                        key={code}
                        className={`inline-flex items-center rounded-lg border-2 border-[#389E0D] bg-green-50 text-[#389E0D] font-medium ${sizing}`}
                        title={cat.label}
                    >
                        {cat.icon && <span>{cat.icon}</span>}
                        <span className="font-bold">{cat.code}</span>
                        {showLabel && (
                            <span className="hidden sm:inline text-xs opacity-75">- {cat.label}</span>
                        )}
                    </span>
                );
            })}
        </div>
    );
}
